// src/hooks/useTables.tsx
//
// Fetches all table data for a patient, including:
// - Event tables (cancer-specific events)
// - Static tables (cancer-specific diagnosis)
// - Patient profile (universal demographics - shared across cancers)
// - Molecular profile (pan-cancer genetic data - shared across cancers)
//
import { createData, fetchData } from "api/apiHelpers";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import logger from "utils/logger";

type DomainTablesData = {
  event: any[];
  static: any[];
};

type UniversalTablesData = {
  patientProfile: any[];
  molecularProfile: any[];
  cancerProfile: any[];
  cancerObservation: any[];
  hematologicalProfile: any[];
  trialEnrollment: any[];
  adverseEvents: any[];
  medications: any[];
  diagnoses: any[];
  procedures: any[];
  tumorAssessments: any[];
  diseaseStatuses: any[];
  imaging: any[];
  systemicTherapies: any[];
  labResults: any[];
  familyHistory: any[];
};

type TablesData = {
  domains: { [key: string]: DomainTablesData };
  universal: UniversalTablesData;
};

// Follow-up refetch delays (ms) after an immediate refresh, for universal
// event-style tables whose rows carry an async `resolution_status` (the
// terminology resolver, cursor_logs/059). A medication created/edited by the
// user starts `resolution_status: "pending"` and is resolved by a Celery task
// ~1s later, so the immediate refetch can't yet show the resolved codes
// (medication_atc / medication_display / atc_class / resolution_status). We
// follow up at ~2s then ~5s (2 extra polls, 3 total incl. the immediate one),
// stopping early once nothing is pending. Kept intentionally bounded — never an
// open-ended poll loop.
const RESOLVER_FOLLOWUP_DELAYS = [2000, 3000];

// True when any universal event-style row is still awaiting async resolution.
// Today only medications carry `resolution_status`; any future universal event
// table (procedures / diagnosis) that adds the field is covered automatically.
const hasPendingResolution = (data: TablesData): boolean =>
  (data.universal?.medications ?? []).some(
    (r: any) => r?.resolution_status === "pending"
  );

const useTables = (selectedDomains: string[] = []) => {
  const { id } = useParams();
  const [tablesData, setTablesData] = useState<TablesData>({
    domains: {},
    universal: { patientProfile: [], molecularProfile: [], cancerProfile: [], cancerObservation: [], hematologicalProfile: [], trialEnrollment: [], adverseEvents: [], medications: [], diagnoses: [], procedures: [], tumorAssessments: [], diseaseStatuses: [], imaging: [], systemicTherapies: [], labResults: [], familyHistory: [] },
  });

  // Keep latest domains in a ref to avoid stale closures
  const domainsRef = useRef<string[]>(selectedDomains);

  useEffect(() => {
    domainsRef.current = selectedDomains.filter(Boolean); // always clean
  }, [selectedDomains]);

  // Generate event + static tables
  const generateTables = async () => {
    // Skip if no domains selected
    const domains = domainsRef.current;
    if (!domains || domains.length === 0) {
      logger.debug("[Tables] No domains selected, skipping table generation");
      return;
    }

    try {
      const payload = { domain_keys: domains, patient_id: id };
      await createData("/event-tables/create/", payload);
      logger.debug("[Tables] Table generation started");
      // NOTE: pollTablesData() was previously called here without await, creating
      // a fire-and-forget race condition with the pollTablesData() call in
      // handleSubmit() (PatientDetails.tsx). The caller (handleSubmit) already
      // calls pollTablesData() after the appropriate delay, so this is redundant.
    } catch (error) {
      logger.error("Error generating tables:", error);
    }
  };

  // Poll tables for each domain - returns the new data for immediate use
  const pollTablesData = useCallback(async (): Promise<TablesData> => {
    const cleanDomains = domainsRef.current;
    const newDomainsData: { [key: string]: DomainTablesData } = {};

    // Fetch every table CONCURRENTLY. These requests are all independent (keyed
    // only by patient id), so awaiting them one-by-one stacked ~13 network
    // round-trips serially and dominated patient-page load time (it grew with
    // each universal table added). Fire them together instead. A missing table
    // (endpoint 404 / not migrated) resolves to [] via safeFetch, preserving the
    // old per-table try/catch tolerance. NOTE: the dev runserver is single
    // -threaded so it still processes these serially, but the client no longer
    // waits a full round-trip before sending the next; under the multi-worker
    // prod server they truly parallelize. (Joan 2026-07-16.)
    const safeFetch = (path: string): Promise<any[]> =>
      fetchData<any[]>(path).catch(() => [] as any[]);

    const [
      domainResults,
      patientProfileData,
      molecularProfileData,
      cancerProfileData,
      cancerObservationData,
      hematologicalProfileData,
      trialEnrollmentData,
      adverseEventsData,
      medicationsData,
      diagnosesData,
      proceduresData,
      tumorAssessmentsData,
      diseaseStatusesData,
      imagingData,
      systemicTherapiesData,
      labResultsData,
      familyHistoryData,
    ] = await Promise.all([
      // Cancer-specific per-domain event/static tables (each domain's pair also
      // fetched concurrently).
      Promise.all(
        cleanDomains.map(async (domainKey) => {
          const dashed = domainKey.replace(/\./g, "-");
          try {
            const [eventData, staticData] = await Promise.all([
              safeFetch(`/${dashed}-eventtable/?patient=${id}`),
              safeFetch(`/${dashed}-statictable/?patient=${id}`),
            ]);
            return { domainKey, event: eventData, static: staticData };
          } catch (error) {
            logger.error(`Error fetching data for ${domainKey}:`, error);
            return null;
          }
        })
      ),
      // Universal tables (fetched ONCE, not per domain).
      safeFetch(`/patientprofiletable/?patient=${id}`),
      safeFetch(`/molecularprofiletable/?patient=${id}`),
      safeFetch(`/cancerprofiletable/?patient=${id}`),
      safeFetch(`/cancerobservationtable/?patient=${id}`),
      safeFetch(`/hematologicalprofiletable/?patient=${id}`),
      safeFetch(`/trialenrollmenttable/?patient=${id}`),
      safeFetch(`/adverseeventtable/?patient=${id}`),
      safeFetch(`/medicationstatementtable/?patient=${id}`),
      safeFetch(`/diagnosistable/?patient=${id}`),
      safeFetch(`/procedurestable/?patient=${id}`),
      safeFetch(`/tumorassessmenttable/?patient=${id}`),
      safeFetch(`/diseasestatustable/?patient=${id}`),
      safeFetch(`/imagingtable/?patient=${id}`),
      safeFetch(`/systemictherapytable/?patient=${id}`),
      safeFetch(`/labresulttable/?patient=${id}`),
      safeFetch(`/familyhistorytable/?patient=${id}`),
    ]);

    for (const r of domainResults) {
      if (r) newDomainsData[r.domainKey] = { event: r.event, static: r.static };
    }

    const newTablesData: TablesData = {
      domains: newDomainsData,
      universal: {
        patientProfile: patientProfileData,
        molecularProfile: molecularProfileData,
        cancerProfile: cancerProfileData,
        cancerObservation: cancerObservationData,
        hematologicalProfile: hematologicalProfileData,
        trialEnrollment: trialEnrollmentData,
        adverseEvents: adverseEventsData,
        medications: medicationsData,
        diagnoses: diagnosesData,
        procedures: proceduresData,
        tumorAssessments: tumorAssessmentsData,
        diseaseStatuses: diseaseStatusesData,
        imaging: imagingData,
        systemicTherapies: systemicTherapiesData,
        labResults: labResultsData,
        familyHistory: familyHistoryData,
      },
    };

    setTablesData(newTablesData);
    return newTablesData;
  }, [id]);

  // Bounded follow-up timers for async terminology resolution. Cleared on
  // unmount and whenever the patient changes so timers never leak or write a
  // refetch into a stale patient's view.
  const resolverTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearResolverTimers = useCallback(() => {
    resolverTimersRef.current.forEach((t) => clearTimeout(t));
    resolverTimersRef.current = [];
  }, []);
  useEffect(() => () => clearResolverTimers(), [id, clearResolverTimers]);

  const scheduleResolverFollowups = useCallback(
    (attempt: number) => {
      if (attempt >= RESOLVER_FOLLOWUP_DELAYS.length) return;
      const timer = setTimeout(async () => {
        const data = await pollTablesData();
        // Keep going only while something is still pending, and only for as many
        // attempts as we have configured delays — always terminates.
        if (hasPendingResolution(data)) {
          scheduleResolverFollowups(attempt + 1);
        }
      }, RESOLVER_FOLLOWUP_DELAYS[attempt]);
      resolverTimersRef.current.push(timer);
    },
    [pollTablesData]
  );

  // Refetch now, then — only if an async resolver is still pending — schedule a
  // few bounded follow-up refetches so resolved codes fill in live without a
  // manual reload. Use this from mutation handlers (create/edit of universal
  // event-style records); plain extraction polling keeps using pollTablesData.
  const refreshTablesData = useCallback(async (): Promise<TablesData> => {
    clearResolverTimers();
    const data = await pollTablesData();
    if (hasPendingResolution(data)) {
      scheduleResolverFollowups(0);
    }
    return data;
  }, [pollTablesData, scheduleResolverFollowups, clearResolverTimers]);

  return { tablesData, generateTables, pollTablesData, refreshTablesData };
};

export default useTables;
